import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import Dialog from '@material-ui/core/Dialog';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { updateUI } from 'redux/actions/ui';
import { getUI, getUser } from 'redux/selectors';
import axios from 'axios';
import settings from 'common/configs/setting';

const getApiBase = () => {
    const url = localStorage.getItem('API_URL') || settings.url;
    return url.replace(/\/+$/, '');
};

const EmailChangeDialog = ({ close, open, dispatch, user }) => {
    const [newEmail, setNewEmail] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const _close = useCallback(() => {
        setNewEmail('');
        setCurrentPassword('');
        setError('');
        setLoading(false);
        setShowPassword(false);
        close && close();
    }, [close]);

    const _submit = useCallback(async () => {
        if (!newEmail || !currentPassword) {
            setError('Please fill in all fields.');
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEmail)) {
            setError('Please enter a valid email address.');
            return;
        }
        setLoading(true);
        setError('');

        try {
            await axios.post(
                `${getApiBase()}/api/v2/account/email-change/request/`,
                { new_email: newEmail, current_password: currentPassword },
                {
                    headers: {
                        Authorization: `Token ${user.api_v2_key}`
                    }
                }
            );

            dispatch(
                updateUI({
                    toast: {
                        open: true,
                        message:
                            'Email change requested! Check your new inbox to confirm.',
                        severity: 'success',
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'center'
                        }
                    }
                })
            );
            _close();
        } catch (err) {
            const errMsg =
                err?.response?.data?.error ||
                err?.response?.data?.new_email?.[0] ||
                err?.response?.data?.current_password?.[0] ||
                'Something went wrong. Please try again.';
            setError(errMsg);
        } finally {
            setLoading(false);
        }
    }, [newEmail, currentPassword, dispatch, _close, user.api_v2_key]);

    return (
        <Dialog
            fullWidth={true}
            open={open}
            onClose={_close}
            aria-labelledby="email-change-dialog-title">
            <div
                id="email-change-modal"
                className="modal-box modal-box--delete modal-box--500">
                <div className="delete-modal">
                    <div className="delete-modal__content">
                        <div className="delete-modal__title">Change Email</div>
                        <div className="delete-modal__subtitle">
                            Enter your new email address and current password to
                            request an email change. You&apos;ll receive a
                            confirmation email.
                        </div>

                        <div className="email-change-form">
                            <div className="email-change-form__group">
                                <label
                                    htmlFor="newEmail"
                                    className="email-change-form__label">
                                    New Email Address
                                </label>
                                <input
                                    id="newEmail"
                                    type="email"
                                    className="delete-moadl_text email-input email-change-form__input"
                                    value={newEmail}
                                    autoComplete="off"
                                    onChange={(e) =>
                                        setNewEmail(e.target.value)
                                    }
                                />
                            </div>
                            <div className="email-change-form__group">
                                <label
                                    htmlFor="currentPassword"
                                    className="email-change-form__label">
                                    Current Password
                                </label>
                                <div className="password-input-container">
                                    <input
                                        id="currentPassword"
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        className="delete-moadl_text password-input-field email-change-form__input"
                                        value={currentPassword}
                                        autoComplete="new-password"
                                        onChange={(e) =>
                                            setCurrentPassword(e.target.value)
                                        }
                                    />
                                    <div
                                        className="password-toggle-icon"
                                        onClick={() =>
                                            setShowPassword(!showPassword)
                                        }>
                                        {showPassword ? (
                                            <VisibilityOff />
                                        ) : (
                                            <Visibility />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div
                                style={{
                                    color: '#dc3545',
                                    fontSize: '13px',
                                    marginTop: '8px'
                                }}>
                                {error}
                            </div>
                        )}
                    </div>

                    <div className="delete-modal__actions">
                        <button
                            className="btn btn-outline"
                            onClick={_close}
                            disabled={loading}>
                            Cancel
                        </button>
                        <button
                            className="btn btn-gradient"
                            onClick={_submit}
                            disabled={loading}>
                            {loading ? 'Sending...' : 'Send Confirmation'}
                        </button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

const state = createStructuredSelector({ ui: getUI, user: getUser });
export default connect(state)(EmailChangeDialog);
